/** @prettier */

import {Container, Stack, Card, CardContent, Grid} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams, useLoaderData} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import SiteDeleteDialog from './SiteDeleteDialog';
import SiteEditDialog from './SiteEditDialog';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import CardContents from '../../component/CardContents';
//API import
import {
  getCompositionsSitesBySiteID,
  getWeatherSearch,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<
  apiIF.res.getCompositionsSitesBySiteID | undefined
> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const siteInfo = await getCompositionsSitesBySiteID(params.siteID);

    if (siteInfo['site-authority'] !== 'admin') {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return siteInfo;
  } catch {
    return undefined;
  }
}

/**
 * サイトの情報を表示
 * @returns JSX
 */
const SiteSetting = () => {
  const loaderData = useLoaderData() as
    | apiIF.res.getCompositionsSitesBySiteID
    | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  const trans = useTranslation().t; //翻訳関数

  const [siteInfo, setSiteInfo] =
    useState<apiIF.reqParameters.putCompositionsSites | null>(null); //サイトの情報
  const [locationDisplay, setLocationDisplay] = useState(''); //天気予報用地域（表示用）
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //保存ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [siteBillingStatus, setSiteBillingStatus] =
    useState<apiIF.res.getCompositionsSitesBySiteID['billing-status']>(
      'unpaid'
    ); //サイトの課金状態

  useEffect(() => {
    if (loaderData === undefined) return;
    setSiteInfo(loaderData);
    setSiteBillingStatus(loaderData['billing-status']);
  }, [loaderData]);

  useEffect(() => {
    if (siteInfo === null) return;
    if ('location' in siteInfo === false) return;
    //天気予報用地域を表示用の値を検索
    getWeatherSearch({query: siteInfo.location}).then(result => {
      if (result['search-result'].length > 0) {
        setLocationDisplay(
          `${result['search-result'][0]['name']}, ${result['search-result'][0]['region']}, ${result['search-result'][0]['country']}`.replace(
            ', ,',
            ','
          )
        );
      } else {
        setLocationDisplay('');
      }
    });
  }, [siteInfo]);

  /**
   * 歯車メニューリスト
   * @returns メニュー一覧
   */
  const gearMenuItem = (): GearMenuListArgType['menuItem'] => {
    return [
      {
        label: trans('SiteSetting.menuItem.edit'),
        onClick: () => {
          setIsOpenEditDialog(true);
        },
        icon: {
          className: 'siteSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(`SiteSetting.image.alternativeText.${iconBlack.EDIT.alt}`),
        },
      },
      {
        label: trans('SiteSetting.menuItem.delete'),
        onClick: () => {
          setIsOpenDeleteDialog(true);
        },
        icon: {
          className: 'SiteSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `SiteSetting.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined || siteInfo === null) return <></>;
  return (
    <Container fixed className="siteSetting">
      <Grid container spacing={3}>
        {/** タイトル */}
        <Grid item xs={12}>
          <h1>{trans('SiteSetting.title.siteSetting')}</h1>
        </Grid>
        {/** 一覧 */}
        <Grid item xs={12} container justifyContent="center">
          <Grid item xs={9}>
            <Card>
              <CardContent>
                <Stack spacing={4}>
                  <Grid
                    container
                    className="siteSetting__contents__title"
                    spacing={1}
                    justifyContent="flex-end">
                    {/* サイト名 */}
                    <Grid
                      item
                      xs
                      className="siteSetting__contents__title__siteName">
                      {siteInfo['site-name']}
                    </Grid>
                    {/* 歯車アイコン */}
                    <Grid item xs="auto">
                      <GearMenu
                        id={`mv-btnSiteSettingOpen`}
                        menuItem={gearMenuItem()}
                      />
                      <SiteEditDialog
                        displaySiteInfo={siteInfo}
                        setSiteInfo={setSiteInfo}
                        isOpenDialog={isOpenEditDialog}
                        setIsOpenDialog={setIsOpenEditDialog}
                      />
                      <SiteDeleteDialog
                        siteName={siteInfo['site-name']}
                        siteBillingStatus={siteBillingStatus}
                        isOpenDialog={isOpenDeleteDialog}
                        setIsOpenDialog={setIsOpenDeleteDialog}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    spacing={2}
                    className="siteSetting__contents__information">
                    {/* 都市 */}
                    <CardContents
                      class="siteSetting__contents__information__underLine"
                      valueDescription={trans(
                        'SiteSetting.txtField.label.city'
                      )}
                      value={siteInfo['city']}
                      valueID={`mv-txtSiteSettingCity`}
                    />
                    {/* 地名（天気予報用） */}
                    <CardContents
                      class="siteSetting__contents__information__underLine"
                      valueDescription={trans(
                        'SiteSetting.txtField.label.location'
                      )}
                      value={locationDisplay}
                      valueID={`mv-txtSiteSettingLocation`}
                    />
                    {/* タイムゾーン */}
                    <CardContents
                      class="siteSetting__contents__information__underLine"
                      valueDescription={trans(
                        'SiteSetting.txtField.label.timezone'
                      )}
                      value={siteInfo['timezone']}
                      valueID={`mv-txtSiteCitySetting`}
                    />
                    {/* 地上階 */}
                    <CardContents
                      class="siteSetting__contents__information__underLine"
                      valueDescription={trans(
                        'SiteSetting.txtField.label.aboveground'
                      )}
                      value={`${trans(
                        `SiteSetting.item.value.aboveground${siteInfo['above-ground']}`
                      )}`}
                      valueID={`mv-txtSiteCitySetting`}
                    />
                    {/* 地下階 */}
                    <CardContents
                      class="siteSetting__contents__information__underLine"
                      valueDescription={trans(
                        'SiteSetting.txtField.label.basement'
                      )}
                      value={`${trans(
                        `SiteSetting.item.value.underground${siteInfo['under-ground']}`
                      )}`}
                      valueID={`mv-txtSiteCitySetting`}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};
export default SiteSetting;
